/** NotificationBell.js:
 *  Displays a notification bell icon with the number of unread notifications.
 *  On click, opens a popover with a list of join requests using the FollowRequestCard component.
 */

// ===============================
// Imports
// ===============================
import React, { useState } from "react";

import {
    IconButton,
    Badge,
    Popover,
    Box,
    Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";

import { doc, updateDoc, arrayUnion, getDoc, addDoc, collection, serverTimestamp, query, getDocs, where } from "firebase/firestore";
import { db } from "../firebase/firebase";

import { useNotifications } from "../contexts/NotificationsContext";
import {
    deleteNotification,
    markNotificationAsRead,
} from "../firebase/services/NotificationService";
import NotificationCard from "./FollowRequestCard";
import "./NotificationBell.css";
import { useUser } from "../contexts/UserProvider";
import InProcessPopup from "./InProcessPopup";
import IndividualTripModal from "./IndividualTripModal";
import { respondToJoinRequest, findJoinRequestId, deleteOtherJoinRequestsForSingleTrip } from "../firebase/services/JoinRequests";
import TripDetailsModal from "./TripCard";
import { createPortal } from "react-dom";
import { useDocumentData } from "react-firebase-hooks/firestore";

// ===============================
// Component: NotificationBell
// ===============================
const NotificationBell = () => {
    const [anchorEl, setAnchorEl] = useState(null); // Controls popover anchor
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const [isSingle, setIsSingle] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const { profile } = useUser();

    const {
        notifications,
        markAllAsRead,
        unreadCount,
        removeNotification,
    } = useNotifications();

    const handleBellClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    // handleTripClick: Fetch the selected trip document from Firestore.
    // If its status is "in-process", open the InProcessPopup, otherwise open the full trip modal.
    const handleTripClick = async (notif) => {
        const { tripId, type } = notif;

        // Choose the appropriate collection based on the type of notification.
        const tripCollection = type === "group_join_request" ? "tripEvents" : "SingleUserTrips";

        const tripDoc = await getDoc(doc(db, tripCollection, tripId));
        if (!tripDoc.exists()) {
            console.warn("Trip not found");
            return;
        }

        const trip = { id: tripDoc.id, ...tripDoc.data() };
        setSelectedTrip(trip);

        // Trip in progress status → Open a custom pop-up
        if (trip.status === "in-process") {
            setInProgress(true);
        } else {
            setInProgress(false);
        }
        setIsSingle(type !== "group_join_request")
        setIsPopupOpen(true);
    };

    const handleClose = async () => {
        setAnchorEl(null);

        const unreadNotifications = notifications.filter((n) => !n.isRead);
        for (const notif of unreadNotifications) {
            try {
                await markNotificationAsRead(notif.id);
            } catch (error) {
                console.error("שגיאה בסימון כנקראה:", error);
            }
        }

        markAllAsRead();
    };

    // handleApprove: When the user approves a join request,
    // update the trip's participants and set its status to "in-process".
    const handleApprove = async (event, notif) => {
        event.stopPropagation();

        try {
            const tripId = notif.tripId;
            const requesterId = notif.fromUserId;

            // Use correct collection based on notification type
            const tripCollection =
                notif.type === "group_join_request" ? "tripEvents" : "SingleUserTrips";

            const tripRef = doc(db, tripCollection, tripId);
            const tripSnap = await getDoc(tripRef);
            if (!tripSnap.exists()) {
                console.warn("הטיול לא נמצא במסד הנתונים");
                return;
            }

            // Check if single trip already has a participant or is in-process
            if (notif.type !== "group_join_request") {
                const tripData = tripSnap.data();
                // Exclude creator and current requester from participants check
                const otherParticipants = (Array.isArray(tripData.participants) ? tripData.participants : [])
                    .filter(
                        (uid) => uid !== profile.id && uid !== requesterId
                    );
                const isInProcess = tripData.status === "in-process";
                if (otherParticipants.length > 0 || (isInProcess && tripData.participants && tripData.participants.length > 0 && !tripData.participants.includes(requesterId))) {
                    console.log('Blocked approval: updating notification', notif, 'with id', notif.id);
                    await updateDoc(doc(db, "notifications", notif.id), {
                        message: "בקשה אחרת להצטרף לטיול זה כבר אושרה",
                        type: "join_rejected_full_single",
                        isRead: false,
                    });
                    // Clean up: delete any other notifications for this user/trip except notif.id
                    const q = query(
                        collection(db, "notifications"),
                        where("tripId", "==", tripId),
                        where("fromUserId", "==", requesterId)
                    );
                    const snapshot = await getDocs(q);
                    snapshot.forEach((docSnap) => {
                        if (docSnap.id !== notif.id) {
                            docSnap.ref.delete();
                        }
                    });
                    // Optionally, update the local state if needed (if not using Firestore listener)
                    // No need to delete or remove notification
                    // Optionally, also reject the join request in joinRequests
                    const requestId = await findJoinRequestId(tripId, requesterId);
                    if (requestId) {
                        await respondToJoinRequest(requestId, "rejected");
                    }
                    return;
                }
            }

            // Delete the notification
            await deleteNotification(notif.id);
            removeNotification(notif.id);

            // Find the requestId
            const requestId = await findJoinRequestId(tripId, requesterId);
            if (!requestId) {
                console.warn("לא נמצא request תואם");
                return;
            }

            // Update the status to "approved"
            await respondToJoinRequest(requestId, "approved");

            // Group trip → just add participant
            // Single user trip → also update status
            const updates = {
                participants: arrayUnion(requesterId),
            };

            if (notif.type !== "group_join_request") {
                updates.status = "in-process";
                // Delete all other join requests for this single trip
                await deleteOtherJoinRequestsForSingleTrip(tripId, requesterId);
            }

            await updateDoc(tripRef, updates);

            // Create a notification for the join requester
            await addDoc(collection(db, "notifications"), {
                userId: requesterId,
                type: notif.type === "group_join_request"
                    ? "join_approved_group"
                    : "join_approved_single",
                tripId: tripId,
                fromUserId: profile.id, // The approver is the current user
                senderName: profile.title || profile.name || "משתמש",
                senderImage: profile.image || "",
                message:
                    notif.type === "group_join_request"
                        ? `${profile.title || profile.name || "משתמש"} אישר את הבקשה שלך להצטרף לטיול הקבוצתי`
                        : `${profile.title || profile.name || "משתמש"} אישר את הבקשה שלך להצטרף לטיול`,
                isRead: false,
                createdAt: serverTimestamp(),
            });

            console.log("הטיול עודכן בהצלחה");
        } catch (error) {
            console.error("שגיאה באישור בקשה:", error);
        }
    };

    // handleReject: When the user rejects a join request,
    const handleReject = async (event, notif) => {
        event.stopPropagation(); // Prevents the click from moving to other elements like the popover

        try {

            const tripId = notif.tripId;
            const requesterId = notif.fromUserId;

            // Delete the notification
            await deleteNotification(notif.id); // Deletes the notification from the database (Firebase)
            removeNotification(notif.id); // Deletes the notification also from the local state (contexts/NotificationsContext)

            // Find the requestId
            const requestId = await findJoinRequestId(tripId, requesterId);
            if (!requestId) {
                console.warn("לא נמצא request תואם");
                return;
            }

            // Update the status to "rejected"
            await respondToJoinRequest(requestId, "rejected");

            console.log("התראה נמחקה בהצלחה (נדחתה)");
        } catch (error) {
            console.error("שגיאה במחיקת ההתראה בעת דחייה:", error);
        }
    };

    const handleDelete = async (notif) => {
        try {
            await deleteNotification(notif.id);
            removeNotification(notif.id);
        } catch (error) {
            console.error("שגיאה במחיקת התראה:", error);
        }
    };

    return (
        <>
            <IconButton
                color="inherit"
                onClick={handleBellClick}
                className="notification-bell"
                aria-label="פתח התראות"
            >
                <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Popover
                open={Boolean(anchorEl)}
                style={{ zIndex: 200 }}
                onClose={handleClose}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Box className="notification-popover" sx={{ zIndex: 10 }}>
                    <Typography className="notification-title">התראות</Typography>

                    {notifications.length === 0 ? (
                        <Typography className="notification-empty">
                            אין התראות
                        </Typography>
                    ) : (
                        [...notifications]
                            .sort((a, b) => b.timestamp - a.timestamp)
                            .map((notif) => {
                                const isApprovalNotif =
                                    notif.type === "join_approved_single" ||
                                    notif.type === "join_approved_group";

                                return (
                                    <NotificationCard
                                        key={notif.id}
                                        profile={{
                                            id: notif.fromUserId,
                                            image: notif.senderImage || "/default-avatar.png",
                                            title: notif.senderName || "משתמש",
                                        }}
                                        isUnread={!notif.isRead}
                                        message={notif.message}
                                        onApprove={!isApprovalNotif ? (e) => handleApprove(e, notif) : undefined}
                                        onDeny={!isApprovalNotif ? (e) => handleReject(e, notif) : undefined}
                                        onDelete={() => handleDelete(notif)}
                                        onCardClick={() => handleTripClick(notif)}
                                        type={notif.type}
                                    />
                                );
                            })
                    )}
                </Box>
            </Popover>

            <TripModal trip={selectedTrip} isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} currentUserId={profile.id} inProgress={inProgress} isSingle={isSingle} />
        </>
    );
};

const TripModal = ({ trip, isOpen, inProgress, isSingle, onClose, currentUserId }) => {
    if (isSingle) {
        return inProgress ?
            createPortal(<InProcessPopup
                isOpen={isOpen}
                style={{ zIndex: 1400 }}
                onClose={onClose}
                profile={trip} />, document.body) :
            (<IndividualTripModal currentUserId={currentUserId}
                profile={{
                    ...trip?.userInfo,
                    id: trip?.id,
                    createdBy: trip?.createdBy
                }
                }
                formData={trip}
                isNotification={true}
                isOpen={isOpen} onClose={onClose} />)
    }

    return trip != null && <TripDetailsPopup trip={trip} isOpen={isOpen} onClose={onClose} />

}

const TripDetailsPopup = ({ trip, isOpen, onClose, }) => {

    const [tripTemplate, loading] = useDocumentData(doc(db, 'tripTemplates', trip.templateId))
    return !loading && createPortal(
        <TripDetailsModal isOpen={isOpen} onClose={onClose} trip={{ ...trip, ...tripTemplate }} hideCancelButton hideMoreInfo />,
        document.body
    )
}

export default NotificationBell;
